import { updateMemberSubscription } from 'actions/LuxPlusActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import type { FormEventHandler } from 'react'
import { useCallback, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'

/**
 * Shared cancel-renewal reason options, labels and submit handler, used by both the pre-flag
 * cancellation form ({@link LuxPlusManageAccountModalRenewalCancellation}) and the redesigned
 * survey ({@link LuxPlusManageAccountModalRenewalCancellationSurvey}).
 */
export const RENEWAL_CANCELLATION_REASONS = [
  'The annual fee is too expensive',
  'I don’t travel frequently enough',
  'The benefits are not meeting my expectations',
  'I don’t want to be automatically charged',
  'Other (please specify)',
] as const

export type RenewalCancellationReason = typeof RENEWAL_CANCELLATION_REASONS[number]

interface CancelRenewalFormDetails {
  reason?: string
  comment?: string
}

const reasonMessages = defineMessages<RenewalCancellationReason>({
  'The annual fee is too expensive': {
    id: 'SUEvSckO0NAqT2Chk8O8',
    defaultMessage: 'The annual fee is too expensive',
    description: 'Cancellation reason option: cost is too high in the cancellation flow',
  },
  'I don’t travel frequently enough': {
    id: '2U4zFTgvZmndC1IiHxIh',
    defaultMessage: 'I don’t travel frequently enough',
    description: 'Cancellation reason option: infrequent travel in a modal dialog',
  },
  'The benefits are not meeting my expectations': {
    id: '3LvBL4X2dgEEtbVeBqGb',
    defaultMessage: 'The benefits are not meeting my expectations',
    description: 'Cancellation reason option: unsatisfied with benefits',
  },
  'I don’t want to be automatically charged': {
    id: 'dkx0qGuAXod-XkWZerZ5',
    defaultMessage: 'I don’t want to be automatically charged',
    description: 'Cancellation reason option: does not want auto-charge',
  },
  'Other (please specify)': {
    id: 's9fZfnhpITlOwF7irD9b',
    defaultMessage: 'Other (please specify)',
    description: 'Cancellation reason option: other with free-text input',
  },
})

/** Localised label per reason, keyed by the (English) reason value submitted to the backend. */
export function useRenewalCancellationReasonLabels(): Record<RenewalCancellationReason, string> {
  const intl = useIntl()
  return useMemo(
    () => Object.fromEntries(
      RENEWAL_CANCELLATION_REASONS.map((reason) => [reason, intl.formatMessage(reasonMessages[reason])]),
    ) as Record<RenewalCancellationReason, string>,
    [intl],
  )
}

/**
 * Form submit handler that pauses (cancels) the member's renewal, forwarding the optional
 * reason/explanation, then fires the success callback.
 */
export function useRenewalCancellationSubmit(
  onRenewCancelSuccess: () => void,
): FormEventHandler<HTMLFormElement> {
  const dispatch = useAppDispatch()
  return useCallback((event) => {
    event.preventDefault()

    const formObject = formToObject<CancelRenewalFormDetails>(event.currentTarget)

    dispatch(updateMemberSubscription({
      op: 'pause',
      reason: formObject.reason ? {
        category: formObject.reason.startsWith('Other') ? 'Other' : formObject.reason,
        explanation: formObject.comment,
      } : undefined,
    }))

    onRenewCancelSuccess()
  }, [dispatch, onRenewCancelSuccess])
}
